import React, { useEffect, useState, createElement } from 'react';
import { DollarSignIcon, TrendingUpIcon, CalendarIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, XCircleIcon, PrinterIcon, FileTextIcon, BanIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { mockClients, mockInvoices } from '../data/mockData';
import { Visit } from '../types';
import jsPDF from 'jspdf';
import { Pagination } from '../components/ui/Pagination';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
const RECIBO_PREFIX = 'MR20';
const RECIBO_START_NUM = 2601;
// ── Helper: format number without currency symbol for tight PDF cells ────────
function fmtNum(n: number): string {
  return n.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
// ── Helper: calculate real ITBIS from facturasDetalle instead of assuming 18% ──
// If facturasDetalle has stored subTotal/itbis per invoice, use proportional ITBIS
// based on how much of each invoice was paid. Falls back to 0 if no ITBIS data.
function getRealItbisFromVisit(visit: Visit): {
  subTotal: number;
  itbis: number;
} {
  const montoTotal = visit.cobro.montoCobrado;
  const facturasDetalle: any[] = (visit.cobro as any).facturasDetalle || [];
  if (facturasDetalle.length > 0) {
    // Check if facturasDetalle has stored itbis data
    const hasItbisData = facturasDetalle.some((fd: any) => fd.subTotal !== undefined || fd.itbis !== undefined);
    if (hasItbisData) {
      // Calculate proportional ITBIS based on what was paid per invoice
      let totalItbis = 0;
      facturasDetalle.forEach((fd: any) => {
        const invSubTotal = fd.subTotal || 0;
        const invItbis = fd.itbis || 0;
        const invMonto = invSubTotal + invItbis;
        if (invMonto > 0 && invItbis > 0) {
          // Proportion of invoice that was paid (using abono = original amount before discount)
          const abono = fd.abono || fd.efectivoPagado || 0;
          const proportion = Math.min(abono / invMonto, 1);
          totalItbis += Math.round(invItbis * proportion * 100) / 100;
        }
        // If invItbis is 0, this invoice contributes 0 ITBIS
      });
      const subTotal = Math.round((montoTotal - totalItbis) * 100) / 100;
      return {
        subTotal: Math.max(0, subTotal),
        itbis: totalItbis
      };
    }
  }
  // Fallback for old data without itbis info: assume no ITBIS (safer than assuming 18%)
  // Old behavior was: subTotal = montoTotal / 1.18, itbis = montoTotal - subTotal
  // But this was wrong for invoices without ITBIS. Default to no ITBIS.
  return {
    subTotal: montoTotal,
    itbis: 0
  };
}
async function printRecibo(visit: Visit, receiptNumber: string) {
  const pw = 80;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [pw, 297] // use longer page to avoid overflow, will trim
  });
  const ml = 4;
  const mr = 4;
  const cw = pw - ml - mr; // 72mm
  const cx = pw / 2;
  const fmtCur = (n: number) => 'DOP ' + fmtNum(n);
  const fmtDate = (d: Date) => new Date(d).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const dashedLine = (yPos: number) => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(ml, yPos, pw - mr, yPos);
    doc.setLineDashPattern([], 0);
  };
  const drawCheckbox = (x: number, yPos: number, checked: boolean) => {
    const size = 3;
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    doc.rect(x, yPos - size, size, size, 'FD');
    if (checked) {
      doc.setDrawColor(0, 100, 0);
      doc.setLineWidth(0.5);
      doc.line(x + 0.5, yPos - 1.5, x + 1.2, yPos - 0.5);
      doc.line(x + 1.2, yPos - 0.5, x + 2.7, yPos - 2.7);
      doc.setDrawColor(0);
      doc.setLineWidth(0.3);
    }
  };
  let y = 2;
  // ── LOGO + HEADER ─────────────────────────────────────────────────────────
  const logoBase64 = await getLogoBase64();
  if (logoBase64) {
    const logoSize = 14;
    doc.addImage(logoBase64, 'PNG', cx - logoSize / 2, y, logoSize, logoSize);
    y += logoSize + 2;
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MILADYS RODRIGUEZ & ASO.', cx, y, {
    align: 'center'
  });
  y += 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av. Antonio Guzmán No.102 • 809-247-1006', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('Santiago, Rep. Dom.', cx, y, {
    align: 'center'
  });
  y += 4;
  doc.text('RNC: 130-329291', cx, y, {
    align: 'center'
  });
  y += 5;
  dashedLine(y);
  y += 5;
  // ── RECEIPT NUMBER & DATE ─────────────────────────────────────────────────
  const receiptNum = receiptNumber;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('RECIBO No: ' + receiptNum, ml, y);
  y += 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Fecha: ' + fmtDate(visit.fecha), ml, y);
  y += 5;
  dashedLine(y);
  y += 5;
  // ── CLIENT ────────────────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Recibido de:', ml, y);
  y += 4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  // Wrap long client names
  const clientLines = doc.splitTextToSize(visit.clienteNombre, cw);
  clientLines.forEach((line: string) => {
    doc.text(line, ml, y);
    y += 4;
  });
  y += 1;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Para ser abonado a su cuenta MILADYS RODRIGUEZ &', ml, y);
  y += 3.5;
  doc.text('Asociados, en la siguiente forma:', ml, y);
  y += 5;
  // ── INVOICE TABLE — 4 cols (wider, mobile-friendly) ────────────────────────
  const tCols = ['FACTURA', 'FECHA', 'VALOR', 'MONTO'];
  const tWidths = [18, 16, 19, 19];
  const tRowH = 6;
  // Header row
  doc.setFillColor(255, 255, 255);
  doc.rect(ml, y, cw, tRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(ml, y, cw, tRowH, 'S');
  doc.setLineWidth(0.5);
  doc.line(ml, y + tRowH, ml + cw, y + tRowH);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  let tx = ml;
  tCols.forEach((col, i) => {
    doc.text(col, tx + tWidths[i] / 2, y + tRowH - 1.5, {
      align: 'center'
    });
    tx += tWidths[i];
  });
  y += tRowH;
  // Data rows
  const facturasDetalle: any[] = (visit.cobro as any).facturasDetalle || [];
  const invoiceNums = visit.cobro.numeroFactura ? visit.cobro.numeroFactura.split(',').map(f => f.trim()).filter(Boolean) : [];
  const dataRows = facturasDetalle.length > 0 ? facturasDetalle : invoiceNums.length > 0 ? invoiceNums.map(n => ({
    numeroFactura: n,
    abono: 0,
    efectivoPagado: 0,
    descuento: 0,
    saldoNuevo: -1
  })) : [{
    numeroFactura: '—',
    abono: 0,
    efectivoPagado: 0,
    descuento: 0,
    saldoNuevo: -1
  }];
  // ── NC APPLICATIONS — pre-query to know which invoices have NCs ──────────
  let ncRows: any[] = [];
  const ncInvoiceNums = new Set<string>();
  // ── PP% from DB — fetch current pp_pct for invoices (may have been edited after visit) ──
  const invoicePPMap: Record<string, {
    ppPct: number | null;
    subTotal: number;
  }> = {};
  try {
    const factNums = dataRows.map((r: any) => typeof r === 'string' ? r : r.numeroFactura).filter((n: string) => n && n !== '—');
    if (factNums.length > 0) {
      const {
        data: ncApps
      } = await supabaseAdmin.from('nc_applications').select('*').eq('farmacia', visit.clienteNombre).in('invoice_numero', factNums).order('created_at', {
        ascending: false
      });
      if (ncApps && ncApps.length > 0) {
        ncRows = ncApps;
        ncApps.forEach((nc: any) => {
          if (nc.invoice_numero) ncInvoiceNums.add(nc.invoice_numero);
        });
      }
      // Fetch current pp_pct and sub_total from invoices table
      const {
        data: invRows
      } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', factNums);
      if (invRows) {
        invRows.forEach((row: any) => {
          invoicePPMap[row.numero_factura] = {
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
            subTotal: Number(row.sub_total) || 0
          };
        });
      }
    }
  } catch {
    // Silently continue if query fails
  }
  let totalPPDiscount = 0;
  dataRows.forEach((row: any, idx: number) => {
    const inv = typeof row === 'string' ? row : row.numeroFactura;
    const hasNC = ncInvoiceNums.has(inv);
    const isEven = idx % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, cw, tRowH, 'F');
    doc.setDrawColor(200, 210, 220);
    doc.setLineWidth(0.15);
    doc.rect(ml, y, cw, tRowH, 'S');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(hasNC ? 7 : 6);
    doc.setFont('helvetica', 'bold');
    // FACTURA — left-aligned with padding
    const maxFacW = tWidths[0] - 2;
    let facTxt = inv;
    while (doc.getTextWidth(facTxt) > maxFacW && facTxt.length > 3) facTxt = facTxt.slice(0, -1);
    if (facTxt !== inv) facTxt = facTxt.slice(0, -1) + '…';
    doc.text(facTxt, ml + 1, y + tRowH - 1.8);
    doc.setFont('helvetica', hasNC ? 'bold' : 'normal');
    // FECHA
    doc.text(fmtDate(visit.fecha), ml + tWidths[0] + tWidths[1] / 2, y + tRowH - 1.8, {
      align: 'center'
    });
    // VALOR (abono / crédito) — BOLD for thermal printer clarity
    const valorFila = row.abono > 0 ? row.abono : idx === 0 ? visit.cobro.credito : 0;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text(valorFila > 0 ? fmtNum(valorFila) : '—', ml + tWidths[0] + tWidths[1] + tWidths[2] - 1, y + tRowH - 1.8, {
      align: 'right'
    });
    // Calculate discount BEFORE rendering MONTO so we can show adjusted amount
    let descFila = row.descuento || 0;
    if (descFila === 0 && inv && invoicePPMap[inv]) {
      const ppData = invoicePPMap[inv];
      if (ppData.ppPct != null && ppData.ppPct > 0 && ppData.subTotal > 0) {
        descFila = Math.round(ppData.subTotal * (ppData.ppPct / 100) * 100) / 100;
      }
    }
    totalPPDiscount += descFila;
    // MONTO (efectivo pagado — adjusted for discount)
    let montofila = row.efectivoPagado > 0 ? row.efectivoPagado : idx === 0 ? visit.cobro.montoCobrado : 0;
    // If there's a retroactive discount and the saved efectivoPagado didn't account for it,
    // show the adjusted amount (monto - descuento)
    if (descFila > 0 && row.descuento === 0 && montofila > 0) {
      montofila = Math.round((montofila - descFila) * 100) / 100;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text(montofila > 0 ? fmtCur(montofila) : '—', ml + tWidths[0] + tWidths[1] + tWidths[2] + tWidths[3] - 1, y + tRowH - 1.8, {
      align: 'right'
    });
    y += tRowH;
    // Show discount sub-row
    if (descFila > 0) {
      doc.setFillColor(240, 253, 244);
      doc.rect(ml, y, cw, 4.5, 'F');
      doc.setDrawColor(200, 230, 210);
      doc.setLineWidth(0.1);
      doc.rect(ml, y, cw, 4.5, 'S');
      doc.setFontSize(5.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(5, 120, 60);
      const ppLabel = invoicePPMap[inv]?.ppPct ? ` ${invoicePPMap[inv].ppPct}%` : '';
      doc.text(`  Desc. PP${ppLabel}: -${fmtNum(descFila)}`, ml + 1, y + 3);
      doc.setTextColor(0, 0, 0);
      y += 4.5;
    }
  });
  // ── NC APPLICATIONS — display section ──────────────────────────────────────
  if (ncRows.length > 0) {
    y += 3;
    // NC section header — clear, bold, readable on thermal printer
    const ncHeaderH = 7;
    doc.setFillColor(240, 240, 240);
    doc.rect(ml, y, cw, ncHeaderH, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(ml, y, cw, ncHeaderH, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('NOTAS DE CRÉDITO APLICADAS', cx, y + ncHeaderH / 2 + 1, {
      align: 'center'
    });
    y += ncHeaderH;
    ncRows.forEach((nc: any, idx: number) => {
      const ncRowH = 7;
      doc.setFillColor(idx % 2 === 0 ? 255 : 248, idx % 2 === 0 ? 255 : 250, 255);
      doc.rect(ml, y, cw, ncRowH, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.15);
      doc.rect(ml, y, cw, ncRowH, 'S');
      // NC number — bold, left
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(nc.credit_note_numero || 'NC', ml + 1.5, y + ncRowH / 2 + 1);
      // Fact reference — BOLD, middle
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(0, 0, 0);
      doc.text('F.Fact: ' + (nc.invoice_numero || ''), ml + 20, y + ncRowH / 2 + 1);
      // Amount — bold, right, negative
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text('-' + fmtCur(nc.nc_monto_aplicado || 0), ml + cw - 1.5, y + ncRowH / 2 + 1, {
        align: 'right'
      });
      doc.setTextColor(0, 0, 0);
      y += ncRowH;
    });
  }
  // ── DESCUENTO PP row (if any retroactive discount was calculated) ─────────
  if (totalPPDiscount > 0) {
    y += 2;
    const discRowH = 7;
    doc.setFillColor(240, 253, 244);
    doc.rect(ml, y, cw, discRowH, 'F');
    doc.setDrawColor(5, 150, 80);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, cw, discRowH, 'S');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('DESCUENTO PP:', ml + 3, y + discRowH / 2 + 1, {
      align: 'left'
    });
    doc.setFontSize(8);
    doc.text('-' + fmtCur(totalPPDiscount), ml + cw - 3, y + discRowH / 2 + 1, {
      align: 'right'
    });
    doc.setTextColor(0, 0, 0);
    y += discRowH;
  }
  // ── SUB TOTAL & ITBIS rows ───────────────────────────────────────────────
  const montoTotal = visit.cobro.montoCobrado;
  // Try to get real ITBIS from stored data first
  let {
    subTotal,
    itbis
  } = getRealItbisFromVisit(visit);
  // If no stored ITBIS data, query the actual invoices from DB
  if (itbis === 0 && montoTotal > 0) {
    try {
      const factNums = dataRows.map((r: any) => typeof r === 'string' ? r : r.numeroFactura).filter((n: string) => n && n !== '—');
      if (factNums.length > 0) {
        const {
          data: invoiceData
        } = await supabaseAdmin.from('invoices').select('numero_factura, sub_total, itbis, monto').in('numero_factura', factNums);
        if (invoiceData && invoiceData.length > 0) {
          let realItbis = 0;
          invoiceData.forEach((inv: any) => {
            const invItbis = inv.itbis || 0;
            const invMonto = inv.monto || 0;
            if (invItbis > 0 && invMonto > 0) {
              // Find matching factura detalle to get proportion paid
              const fd = dataRows.find((r: any) => r.numeroFactura === inv.numero_factura);
              const abono = fd ? fd.abono || fd.efectivoPagado || 0 : 0;
              const proportion = abono > 0 ? Math.min(abono / invMonto, 1) : 0;
              realItbis += Math.round(invItbis * proportion * 100) / 100;
            }
          });
          if (realItbis > 0) {
            itbis = realItbis;
            subTotal = Math.round((montoTotal - itbis) * 100) / 100;
          }
        }
      }
    } catch {
      // Keep the default values if query fails
    }
  }
  const summaryRowH = 7;
  y += 2;
  // Sub Total row — bold numbers for thermal printer
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, cw, summaryRowH, 'F');
  doc.setDrawColor(180, 190, 200);
  doc.setLineWidth(0.2);
  doc.rect(ml, y, cw, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('SUB TOTAL:', ml + 3, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFontSize(8);
  doc.text(fmtCur(subTotal), ml + cw - 3, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // ITBIS row — bold numbers for thermal printer
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, cw, summaryRowH, 'F');
  doc.setDrawColor(180, 190, 200);
  doc.setLineWidth(0.2);
  doc.rect(ml, y, cw, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('ITBIS 18%:', ml + 3, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFontSize(8);
  doc.text(fmtCur(itbis), ml + cw - 3, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // TOTAL row — extra bold and larger for thermal printer
  const adjustedTotal = Math.max(0, Math.round((visit.cobro.montoCobrado - totalPPDiscount) * 100) / 100);
  const totalRowH = 9;
  doc.setFillColor(235, 240, 255);
  doc.rect(ml, y, cw, totalRowH, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.4);
  doc.rect(ml, y, cw, totalRowH, 'S');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TOTAL:', ml + 3, y + totalRowH / 2 + 1.5, {
    align: 'left'
  });
  doc.setFontSize(10);
  doc.text(fmtCur(adjustedTotal), ml + cw - 3, y + totalRowH / 2 + 1.5, {
    align: 'right'
  });
  y += totalRowH + 5;
  dashedLine(y);
  y += 5;
  // ── PAYMENT METHOD — drawn checkboxes ─────────────────────────────────────
  const metodo = visit.cobro.metodoPago || '';
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Forma de pago:', ml, y);
  y += 6;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  drawCheckbox(ml, y, metodo === 'efectivo');
  doc.text('Efectivo', ml + 4.5, y);
  drawCheckbox(ml + 26, y, metodo === 'cheque');
  doc.text('Cheque N°', ml + 30.5, y);
  if (metodo === 'cheque') {
    // Use cheques[] array if available, otherwise fall back to legacy single-cheque fields
    const chequesArr = visit.cobro.cheques && visit.cobro.cheques.length > 0 ? visit.cobro.cheques : [{
      numeroCheque: visit.cobro.numeroCheque || '',
      bancoCheque: visit.cobro.bancoCheque || '',
      fechaDeposito: visit.cobro.fechaDeposito || '',
      chequeImagen: null
    }];
    // Show first cheque number inline with the checkbox label
    const firstNum = chequesArr[0]?.numeroCheque || '___________';
    doc.setFont('helvetica', 'bold');
    doc.text(firstNum, ml + 48, y);
    doc.setFont('helvetica', 'normal');
    // Render each cheque's details
    chequesArr.forEach((ch, idx) => {
      if (chequesArr.length > 1) {
        // Label for multi-cheque
        y += 5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(`Cheque #${idx + 1}:`, ml + 4, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        if (idx > 0 && ch.numeroCheque) {
          doc.text(ch.numeroCheque, ml + 22, y);
        }
      }
      // Bank
      y += 5;
      doc.text('Banco:', ml + 4, y);
      if (ch.bancoCheque) {
        doc.setFont('helvetica', 'bold');
        doc.text(ch.bancoCheque, ml + 16, y);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.setLineWidth(0.2);
        doc.line(ml + 16, y + 0.5, pw - mr, y + 0.5);
      }
      // Deposit date
      y += 5;
      const fechaDep = ch.fechaDeposito ? new Date(ch.fechaDeposito + 'T00:00:00').toLocaleDateString('es-DO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) : '';
      doc.text('Fecha depósito:', ml + 4, y);
      if (fechaDep) {
        doc.setFont('helvetica', 'bold');
        doc.text(fechaDep, ml + 28, y);
        doc.setFont('helvetica', 'normal');
      } else {
        doc.setLineWidth(0.2);
        doc.line(ml + 28, y + 0.5, pw - mr, y + 0.5);
      }
    });
  } else {
    doc.setLineWidth(0.2);
    doc.line(ml + 50, y + 0.5, pw - mr, y + 0.5);
  }
  y += 6;
  drawCheckbox(ml, y, metodo === 'transferencia');
  doc.text('Transferencia', ml + 4.5, y);
  if (metodo === 'transferencia') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 100, 50);
    doc.text('✓ Confirmada', ml + 30, y);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }
  y += 8;
  dashedLine(y);
  y += 7;
  // ── SIGNATURE LINES ───────────────────────────────────────────────────────
  const sigW = 28;
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + sigW, y);
  doc.line(pw - mr - sigW, y, pw - mr, y);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Conforme Cliente', ml + sigW / 2, y + 4, {
    align: 'center'
  });
  doc.text('Recibido Conforme', pw - mr - sigW / 2, y + 4, {
    align: 'center'
  });
  y += 10;
  dashedLine(y);
  y += 6;
  // ── FOOTER ────────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('--- Gracias por su pago ---', cx, y, {
    align: 'center'
  });
  y += 7;
  // Barcode simulation
  const barY = y;
  const barH = 10;
  let bx = ml;
  const seed = receiptNum.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = 0; i < 60; i++) {
    const w = seed * (i + 7) * 13 % 3 * 0.4 + 0.5;
    if (i % 2 === 0) {
      doc.setFillColor(0, 0, 0);
      doc.rect(bx, barY, w, barH, 'F');
    }
    bx += w + 0.3;
    if (bx > ml + cw - 2) break;
  }
  y += barH + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RECIBO: ' + receiptNum, cx, y, {
    align: 'center'
  });
  window.open(String(doc.output('bloburl')), '_blank');
}
export function CollectionsPage() {
  const {
    user
  } = useAuth();
  const {
    visits
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const [isNewCobroOpen, setIsNewCobroOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Retroactive PP discount map: visitId → total discount amount
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    const computeRetroDiscounts = async () => {
      // Collect all invoice numbers from all cobro visits
      const allFactNums: string[] = [];
      const visitFactMap: Record<string, string[]> = {};
      visits.forEach(v => {
        const fd: any[] = (v.cobro as any).facturasDetalle || [];
        if (fd.length > 0) {
          const nums = fd.map((f: any) => f.numeroFactura).filter(Boolean);
          if (nums.length > 0) {
            visitFactMap[v.id] = nums;
            allFactNums.push(...nums);
          }
        }
      });
      if (allFactNums.length === 0) return;
      const uniqueNums = [...new Set(allFactNums)];
      try {
        const {
          data: invRows
        } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
        if (!invRows) return;
        const ppMap: Record<string, {
          ppPct: number | null;
          subTotal: number;
        }> = {};
        invRows.forEach((row: any) => {
          ppMap[row.numero_factura] = {
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
            subTotal: Number(row.sub_total) || 0
          };
        });
        const newMap: Record<string, number> = {};
        Object.entries(visitFactMap).forEach(([visitId, nums]) => {
          const v = visits.find(vis => vis.id === visitId);
          if (!v) return;
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          let totalDisc = v.cobro.descuento || 0;
          // Only compute retro if saved descuento is 0
          if (totalDisc === 0) {
            nums.forEach(num => {
              const pp = ppMap[num];
              const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
              if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
                totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
              }
            });
          }
          if (totalDisc > 0) newMap[visitId] = totalDisc;
        });
        setRetroDiscountMap(newMap);
      } catch {
        // Silently fail
      }
    };
    computeRetroDiscounts();
  }, [visits]);
  // Anular recibo state
  const [anularVisit, setAnularVisit] = useState<Visit | null>(null);
  const [isAnulando, setIsAnulando] = useState(false);
  const [anulados, setAnulados] = useState<Set<string>>(() => {
    // Load from visits that already have anulado flag
    const set = new Set<string>();
    visits.forEach(v => {
      if ((v.cobro as any)?.anulado) set.add(v.id);
    });
    return set;
  });
  const collectionVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta').filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (statusFilter && v.cobro.estadoFactura !== statusFilter) return false;
    return true;
  });
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(collectionVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    recibo: v => receiptNumberMap.get(v.id) || '',
    vendedor: v => v.vendedorNombre,
    cliente: v => v.clienteNombre,
    subTotal: v => getRealItbisFromVisit(v).subTotal,
    itbis: v => getRealItbisFromVisit(v).itbis,
    monto: v => v.cobro.montoCobrado,
    credito: v => v.cobro.credito,
    descuento: v => v.cobro.descuento,
    metodo: v => v.cobro.metodoPago || '',
    factura: v => v.cobro.numeroFactura || '',
    estado: v => v.cobro.estadoFactura || ''
  });
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const activeVisits = finalVisits.filter(v => !(v.cobro as any)?.anulado && !anulados.has(v.id));
  const totalCobrado = activeVisits.reduce((sum, v) => sum + v.cobro.montoCobrado, 0);
  const cobrosPagados = activeVisits.filter(v => v.cobro.estadoFactura === 'pagada_completa').length;
  const cobrosParciales = activeVisits.filter(v => v.cobro.estadoFactura === 'pago_parcial').length;
  const cobrosPendientes = activeVisits.filter(v => v.cobro.estadoFactura === 'no_pago').length;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return '-';
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cheque: 'Cheque'
    };
    return labels[method] || method;
  };
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'pagada_completa',
    label: 'Pagada Completa'
  }, {
    value: 'pago_parcial',
    label: 'Pago Parcial'
  }, {
    value: 'no_pago',
    label: 'Pendiente'
  }];
  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClientId(e.target.value);
    setSelectedInvoices(new Set());
  };
  const pendingInvoices = selectedClientId ? mockInvoices.filter(inv => inv.clientId === selectedClientId && (inv.estado === 'pendiente' || inv.estado === 'vencida' || inv.estado === 'parcial')) : [];
  const toggleInvoiceSelection = (invoiceId: string) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) newSelection.delete(invoiceId);else newSelection.add(invoiceId);
    setSelectedInvoices(newSelection);
  };
  const totalSelectedAmount = pendingInvoices.filter(inv => selectedInvoices.has(inv.id)).reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const handleSubmitCobro = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsNewCobroOpen(false);
    }, 1000);
  };
  // Build a map of visit ID → sequential receipt number (MR202601, MR202602, ...)
  // Use DB field numero_recibo if available, otherwise generate sequentially
  const allCobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0).sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const receiptNumberMap = new Map<string, string>();
  allCobroVisits.forEach((v, idx) => {
    receiptNumberMap.set(v.id, v.numeroRecibo || RECIBO_PREFIX + (RECIBO_START_NUM + idx));
  });
  const getReceiptNumber = (visitId: string) => receiptNumberMap.get(visitId) || '';
  const handlePrintRecibo = async (visit: Visit) => {
    setPrintingId(visit.id);
    try {
      await printRecibo(visit, getReceiptNumber(visit.id));
    } finally {
      setPrintingId(null);
    }
  };
  const handleAnularCobro = async () => {
    if (!anularVisit) return;
    setIsAnulando(true);
    try {
      const updatedCobro = {
        ...anularVisit.cobro,
        anulado: true
      };
      await supabaseAdmin.from('visits').update({
        cobro: updatedCobro
      }).eq('id', anularVisit.id);
      setAnulados(prev => new Set([...prev, anularVisit.id]));
      setAnularVisit(null);
    } catch (err) {
      console.error('Error anulando cobro:', err);
    } finally {
      setIsAnulando(false);
    }
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-680">
      <PageHeader title="Registro de Cobros" subtitle={`${finalVisits.length} registros encontrados`} icon={<DollarSignIcon className="w-6 h-6" data-id="element-682" />} data-id="element-681">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto" data-id="element-683">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-685" />} data-id="element-684" />
          <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-686" />
          <Button variant="secondary" onClick={() => {
          setDateFilter('');
          setStatusFilter('');
        }} data-id="element-687">
            Limpiar
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-688">
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-690" />} variant="success" data-id="element-689" />
        <StatsCard title="Completos" value={cobrosPagados} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-692" />} variant="primary" data-id="element-691" />
        <StatsCard title="Parciales" value={cobrosParciales} icon={<ClockIcon className="w-5 h-5" data-id="element-694" />} variant="warning" data-id="element-693" />
        <StatsCard title="Pendientes" value={cobrosPendientes} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-696" />} variant="danger" data-id="element-695" />
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-697">
        {finalVisits.length === 0 ? <div className="p-12 text-center animate-fade-in" data-id="element-698">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-699">
              <DollarSignIcon className="w-8 h-8 text-slate-400" data-id="element-700" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-701">
              No hay cobros
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-702">
              No hay cobros registrados en el período seleccionado
            </p>
          </div> : <>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-slate-100" data-id="element-703">
              {paginatedVisits.map(visit => {
            const isPagada = visit.cobro.estadoFactura === 'pagada_completa';
            const isParcial = visit.cobro.estadoFactura === 'pago_parcial';
            const isPendiente = visit.cobro.estadoFactura === 'no_pago';
            const isPrinting = printingId === visit.id;
            const montoTotal = visit.cobro.montoCobrado;
            const {
              subTotal,
              itbis
            } = getRealItbisFromVisit(visit);
            return <div key={visit.id} className={`p-4 ${isPendiente ? 'bg-rose-50/40' : ''}`} data-id="element-704">
                    <div className="flex items-start justify-between gap-2" data-id="element-705">
                      <div className="flex-1 min-w-0" data-id="element-706">
                        <p className="font-semibold text-slate-900 truncate" data-id="element-707">
                          {visit.clienteNombre}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-708">
                          {visit.zona} · {formatDate(visit.fecha)}
                          {getReceiptNumber(visit.id) && <span className="ml-1 font-mono font-semibold text-[#2952C4]" data-id="element-709">
                              · {getReceiptNumber(visit.id)}
                            </span>}
                        </p>
                        {isAdmin && <p className="text-xs text-slate-500 mt-0.5" data-id="element-710">
                            {visit.vendedorNombre}
                          </p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-711">
                        {isPagada && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-712">
                            <CheckCircleIcon className="w-3 h-3" data-id="element-713" /> Pagada
                          </span>}
                        {isParcial && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-714">
                            <ClockIcon className="w-3 h-3" data-id="element-715" /> Parcial
                          </span>}
                        {isPendiente && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-716">
                            <XCircleIcon className="w-3 h-3" data-id="element-717" /> Pendiente
                          </span>}
                        {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 && !anulados.has(visit.id) && <>
                              <button onClick={() => handlePrintRecibo(visit)} disabled={isPrinting} className="p-1.5 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors disabled:opacity-40" data-id="element-718">
                                {isPrinting ? <div className="w-4 h-4 border-2 border-[#aac1ef] border-t-[#2952C4] rounded-full animate-spin" data-id="element-719" /> : <PrinterIcon className="w-4 h-4" data-id="element-720" />}
                              </button>
                              <button onClick={() => setAnularVisit(visit)} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-721">
                                <BanIcon className="w-4 h-4" data-id="element-722" />
                              </button>
                            </>}
                        {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-723">
                            <BanIcon className="w-3 h-3" data-id="element-724" /> Anulado
                          </span>}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-id="element-725">
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-726">
                        <p className="text-slate-400 mb-0.5" data-id="element-727">Sub Total</p>
                        <p className="font-bold text-slate-700" data-id="element-728">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(subTotal) : <span className="text-slate-300" data-id="element-729">—</span>}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-730">
                        <p className="text-slate-400 mb-0.5" data-id="element-731">ITBIS 18%</p>
                        <p className="font-bold text-slate-500" data-id="element-732">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(itbis) : <span className="text-slate-300" data-id="element-733">—</span>}
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-2" data-id="element-734">
                        <p className="text-emerald-600 mb-0.5 font-medium" data-id="element-735">
                          Total
                        </p>
                        <p className="font-bold text-emerald-700" data-id="element-736">
                          {visit.cobro.huboCobo && montoTotal > 0 ? formatCurrency(montoTotal) : <span className="text-rose-400" data-id="element-737">Sin cobro</span>}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2" data-id="element-738">
                        <p className="text-slate-400 mb-0.5" data-id="element-739">Método</p>
                        <p className="font-medium text-slate-700" data-id="element-740">
                          {getPaymentMethodLabel(visit.cobro.metodoPago)}
                        </p>
                      </div>
                    </div>
                    {(visit.cobro.credito > 0 || visit.cobro.descuento > 0) && <div className="mt-2 flex gap-2 text-xs" data-id="element-741">
                        {visit.cobro.credito > 0 && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium" data-id="element-742">
                            Crédito: {formatCurrency(visit.cobro.credito)}
                          </span>}
                        {visit.cobro.descuento > 0 && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium" data-id="element-743">
                            Desc: {formatCurrency(visit.cobro.descuento)}
                          </span>}
                      </div>}
                    {visit.motivoNoPago && <p className="mt-2 text-xs text-rose-600" data-id="element-744">
                        ⚠ {visit.motivoNoPago}
                      </p>}
                  </div>;
          })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-745">
              <table className="w-full" data-id="element-746">
                <thead data-id="element-747">
                  <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-748">
                    <SortableTableHeader label="Fecha / Hora" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-749" />
                    <SortableTableHeader label="No. Recibo" sortKey="recibo" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-750" />
                    {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-751" />}
                    <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-752" />
                    <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-753" />
                    <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-754" />
                    <SortableTableHeader label="Monto Cobrado" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-755" />
                    <SortableTableHeader label="Crédito" sortKey="credito" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-756" />
                    <SortableTableHeader label="Descuento" sortKey="descuento" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-757" />
                    <SortableTableHeader label="Método" sortKey="metodo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-758" />
                    <SortableTableHeader label="Factura" sortKey="factura" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-759" />
                    <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-760" />
                    <th className="px-5 py-4 w-12" data-id="element-761"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-762">
                  {paginatedVisits.map(visit => {
                const isPagada = visit.cobro.estadoFactura === 'pagada_completa';
                const isParcial = visit.cobro.estadoFactura === 'pago_parcial';
                const isPendiente = visit.cobro.estadoFactura === 'no_pago';
                const isPrinting = printingId === visit.id;
                const montoTotal = visit.cobro.montoCobrado;
                const {
                  subTotal,
                  itbis
                } = getRealItbisFromVisit(visit);
                return <tr key={visit.id} className={`hover:bg-slate-50/50 transition-colors ${isPendiente ? 'bg-rose-50/30' : ''}`} data-id="element-763">
                        <td className="px-5 py-4" data-id="element-764">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-765">
                            {formatDate(visit.fecha)}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-766">
                            {formatTime(visit.horaLlegada)}
                          </p>
                        </td>
                        <td className="px-5 py-4" data-id="element-767">
                          {getReceiptNumber(visit.id) ? <span className="font-mono text-sm font-semibold text-[#2952C4]" data-id="element-768">
                              {getReceiptNumber(visit.id)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-769">—</span>}
                        </td>
                        {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-770">
                            {visit.vendedorNombre}
                          </td>}
                        <td className="px-5 py-4" data-id="element-771">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-772">
                            {visit.clienteNombre}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-773">{visit.zona}</p>
                          {visit.motivoNoPago && <p className="text-xs text-rose-600 mt-0.5" data-id="element-774">
                              ⚠ {visit.motivoNoPago}
                            </p>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-775">
                          {visit.cobro.huboCobo && montoTotal > 0 ? <span className="text-sm text-slate-600" data-id="element-776">
                              {formatCurrency(subTotal)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-777">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-778">
                          {visit.cobro.huboCobo && montoTotal > 0 ? <span className="text-sm text-slate-500" data-id="element-779">
                              {formatCurrency(itbis)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-780">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-781">
                          {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 ? <span className="text-lg font-bold text-emerald-600" data-id="element-782">
                              {formatCurrency(visit.cobro.montoCobrado - (retroDiscountMap[visit.id] && visit.cobro.descuento === 0 ? retroDiscountMap[visit.id] : 0))}
                            </span> : <span className="text-sm font-medium text-rose-400" data-id="element-783">
                              Sin cobro
                            </span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-784">
                          {visit.cobro.credito > 0 ? <span className="text-sm font-semibold text-blue-600" data-id="element-785">
                              {formatCurrency(visit.cobro.credito)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-786">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-787">
                          {(() => {
                      const disc = retroDiscountMap[visit.id] || visit.cobro.descuento || 0;
                      return disc > 0 ? <span className="text-sm font-semibold text-emerald-600" data-id="element-788">
                                {formatCurrency(disc)}
                              </span> : <span className="text-slate-300 text-sm" data-id="element-789">—</span>;
                    })()}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-790">
                          {visit.cobro.metodoPago ? <span className="text-sm text-slate-600" data-id="element-791">
                              {getPaymentMethodLabel(visit.cobro.metodoPago)}
                            </span> : <span className="text-slate-300 text-sm" data-id="element-792">—</span>}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-793">
                          {visit.cobro.facturasDetalle && visit.cobro.facturasDetalle.length > 0 ? <div className="flex flex-col gap-1.5 items-center" data-id="element-794">
                              {visit.cobro.facturasDetalle!.map((fd, i) => <div key={i} className="w-full text-left border border-slate-100 rounded-lg px-2 py-1.5 bg-slate-50" data-id="element-795">
                                  <div className="flex items-center justify-between gap-2" data-id="element-796">
                                    <span className="text-xs font-bold text-slate-700" data-id="element-797">
                                      {fd.numeroFactura}
                                    </span>
                                    <span className="text-xs font-semibold text-emerald-600" data-id="element-798">
                                      {formatCurrency(fd.efectivoPagado)}
                                    </span>
                                  </div>
                                  {fd.descuento > 0 && <div className="text-xs text-emerald-500 mt-0.5" data-id="element-799">
                                      Desc: {formatCurrency(fd.descuento)}
                                    </div>}
                                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-800">
                                    Saldo:{' '}
                                    {fd.saldoNuevo === 0 ? <span className="text-emerald-500 font-medium" data-id="element-801">
                                        ✓ Pagada
                                      </span> : formatCurrency(fd.saldoNuevo)}
                                  </div>
                                </div>)}
                            </div> : visit.cobro.numeroFactura ? <div className="flex flex-col gap-1 items-center" data-id="element-802">
                              {visit.cobro.numeroFactura.split(',').map((f: string, i: number) => <span key={i} className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-803">
                                    {f.trim()}
                                  </span>)}
                            </div> : <span className="text-slate-300 text-sm" data-id="element-804">—</span>}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-805">
                          {isPagada && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-806">
                              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-807" /> Pagada
                            </span>}
                          {isParcial && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-808">
                              <ClockIcon className="w-3.5 h-3.5" data-id="element-809" /> Parcial
                            </span>}
                          {isPendiente && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-810">
                              <XCircleIcon className="w-3.5 h-3.5" data-id="element-811" /> Pendiente
                            </span>}
                        </td>
                        <td className="px-3 py-4 text-center" data-id="element-812">
                          <div className="flex items-center justify-center gap-1" data-id="element-813">
                            {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 && !anulados.has(visit.id) && <>
                                  <button onClick={() => handlePrintRecibo(visit)} title="Imprimir recibo" disabled={isPrinting} className="p-1.5 text-slate-400 hover:text-[#2952C4] hover:bg-[#eef2fc] rounded-lg transition-colors disabled:opacity-40" data-id="element-814">
                                    {isPrinting ? <div className="w-4 h-4 border-2 border-[#aac1ef] border-t-[#2952C4] rounded-full animate-spin" data-id="element-815" /> : <PrinterIcon className="w-4 h-4" data-id="element-816" />}
                                  </button>
                                  <button onClick={() => setAnularVisit(visit)} title="Anular recibo" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-817">
                                    <BanIcon className="w-4 h-4" data-id="element-818" />
                                  </button>
                                </>}
                            {anulados.has(visit.id) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500" data-id="element-819">
                                <BanIcon className="w-3 h-3" data-id="element-820" /> Anulado
                              </span>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </>}
      </Card>

      <div className="grid grid-cols-3 gap-3 animate-stagger-3" data-id="element-821">
        <Card data-id="element-822">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-823">
            <div className="p-2.5 bg-[#eef2fc] rounded-xl flex-shrink-0" data-id="element-824">
              <TrendingUpIcon className="w-5 h-5 text-[#2952C4]" data-id="element-825" />
            </div>
            <div data-id="element-826">
              <p className="text-xs text-slate-500" data-id="element-827">Efectivo</p>
              <p className="text-base font-bold text-slate-900" data-id="element-828">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'efectivo').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
        <Card data-id="element-829">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-830">
            <div className="p-2.5 bg-indigo-50 rounded-xl flex-shrink-0" data-id="element-831">
              <TrendingUpIcon className="w-5 h-5 text-indigo-600" data-id="element-832" />
            </div>
            <div data-id="element-833">
              <p className="text-xs text-slate-500" data-id="element-834">Transferencia</p>
              <p className="text-base font-bold text-slate-900" data-id="element-835">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'transferencia').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
        <Card data-id="element-836">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-837">
            <div className="p-2.5 bg-violet-50 rounded-xl flex-shrink-0" data-id="element-838">
              <DollarSignIcon className="w-5 h-5 text-violet-600" data-id="element-839" />
            </div>
            <div data-id="element-840">
              <p className="text-xs text-slate-500" data-id="element-841">Cheque</p>
              <p className="text-base font-bold text-slate-900" data-id="element-842">
                {formatCurrency(activeVisits.filter(v => v.cobro.metodoPago === 'cheque').reduce((sum, v) => sum + v.cobro.montoCobrado, 0))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Modal isOpen={isNewCobroOpen} onClose={() => setIsNewCobroOpen(false)} title="Registrar Nuevo Cobro" size="lg" data-id="element-843">
        <ModalBody data-id="element-844">
          <div className="space-y-6" data-id="element-845">
            <div data-id="element-846">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-847">
                Seleccionar Cliente
              </label>
              <div className="relative" data-id="element-848">
                <select value={selectedClientId} onChange={handleClientChange} className="block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-849">
                  <option value="" data-id="element-850">Seleccione una farmacia...</option>
                  {mockClients.map(client => <option key={client.id} value={client.id} data-id="element-851">
                      {client.nombre}
                    </option>)}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-500" data-id="element-852">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-853">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-854" />
                  </svg>
                </div>
              </div>
            </div>

            {selectedClientId && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-855">
                <div className="flex items-center justify-between mb-3" data-id="element-856">
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2" data-id="element-857">
                    <FileTextIcon className="w-4 h-4 text-slate-500" data-id="element-858" /> Facturas
                    Pendientes
                  </h3>
                  {pendingInvoices.length > 0 && <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full" data-id="element-859">
                      {pendingInvoices.length} facturas
                    </span>}
                </div>
                {pendingInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-860">
                    <CheckCircleIcon className="w-10 h-10 text-emerald-400 mx-auto mb-3" data-id="element-861" />
                    <h4 className="text-slate-900 font-medium mb-1" data-id="element-862">
                      Todo al día
                    </h4>
                    <p className="text-slate-500 text-sm" data-id="element-863">
                      Este cliente no tiene facturas pendientes de cobro.
                    </p>
                  </div> : <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-864">
                    <div className="max-h-[300px] overflow-y-auto" data-id="element-865">
                      <table className="w-full text-sm" data-id="element-866">
                        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-867">
                          <tr data-id="element-868">
                            <th className="px-4 py-3 w-10" data-id="element-869">
                              <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={selectedInvoices.size === pendingInvoices.length && pendingInvoices.length > 0} onChange={() => {
                          if (selectedInvoices.size === pendingInvoices.length) setSelectedInvoices(new Set());else setSelectedInvoices(new Set(pendingInvoices.map(i => i.id)));
                        }} data-id="element-870" />
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-871">
                              Factura
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-872">
                              Vencimiento
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-500" data-id="element-873">
                              Estado
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500" data-id="element-874">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white" data-id="element-875">
                          {pendingInvoices.map(inv => {
                      const isSelected = selectedInvoices.has(inv.id);
                      const daysOverdue = Math.floor((new Date().getTime() - new Date(inv.fechaVencimiento).getTime()) / (1000 * 60 * 60 * 24));
                      const isOverdue = daysOverdue > 0;
                      return <tr key={inv.id} className={`transition-colors cursor-pointer ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`} onClick={() => toggleInvoiceSelection(inv.id)} data-id="element-876">
                                <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-877">
                                  <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={isSelected} onChange={() => toggleInvoiceSelection(inv.id)} data-id="element-878" />
                                </td>
                                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-879">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-4 py-3" data-id="element-880">
                                  <div className="text-slate-600" data-id="element-881">
                                    {formatDate(inv.fechaVencimiento)}
                                  </div>
                                  {isOverdue && <div className="text-xs text-rose-600 font-medium mt-0.5" data-id="element-882">
                                      {daysOverdue} días vencida
                                    </div>}
                                </td>
                                <td className="px-4 py-3 text-center" data-id="element-883">
                                  {inv.estado === 'vencida' ? <Badge variant="danger" size="sm" data-id="element-884">
                                      Vencida
                                    </Badge> : inv.estado === 'parcial' ? <Badge variant="warning" size="sm" data-id="element-885">
                                      Parcial
                                    </Badge> : <Badge variant="info" size="sm" data-id="element-886">
                                      Pendiente
                                    </Badge>}
                                </td>
                                <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-887">
                                  {formatCurrency(inv.saldoPendiente)}
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-between" data-id="element-888">
                      <div className="text-sm text-slate-500" data-id="element-889">
                        {selectedInvoices.size} facturas seleccionadas
                      </div>
                      <div className="flex items-center gap-3" data-id="element-890">
                        <span className="text-sm font-semibold text-slate-700" data-id="element-891">
                          Total a Cobrar:
                        </span>
                        <span className="text-xl font-bold text-emerald-600" data-id="element-892">
                          {formatCurrency(totalSelectedAmount)}
                        </span>
                      </div>
                    </div>
                  </div>}
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-893">
          <Button variant="secondary" onClick={() => setIsNewCobroOpen(false)} data-id="element-894">
            Cancelar
          </Button>
          <Button onClick={handleSubmitCobro} isLoading={isSubmitting} disabled={!selectedClientId || selectedInvoices.size === 0} leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-896" />} data-id="element-895">
            Registrar Cobro
          </Button>
        </ModalFooter>
      </Modal>

      {/* Anular Recibo Modal */}
      <Modal isOpen={!!anularVisit} onClose={() => setAnularVisit(null)} title="Anular Recibo de Cobro" size="sm" data-id="element-897">
        <ModalBody data-id="element-898">
          <div className="text-center space-y-3" data-id="element-899">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-900">
              <BanIcon className="w-7 h-7 text-rose-500" data-id="element-901" />
            </div>
            <p className="text-slate-600" data-id="element-902">
              ¿Estás seguro de anular el recibo de cobro de{' '}
              <strong data-id="element-903">{anularVisit?.clienteNombre}</strong> por{' '}
              <strong className="text-emerald-600" data-id="element-904">
                {anularVisit ? formatCurrency(anularVisit.cobro.montoCobrado) : ''}
              </strong>
              ?
            </p>
            <p className="text-xs text-slate-400" data-id="element-905">
              Esta acción marcará el recibo como anulado. No se puede deshacer.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-906">
          <Button variant="secondary" onClick={() => setAnularVisit(null)} data-id="element-907">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularCobro} isLoading={isAnulando} leftIcon={<BanIcon className="w-4 h-4" data-id="element-909" />} data-id="element-908">
            Anular Recibo
          </Button>
        </ModalFooter>
      </Modal>

      <div className="animate-stagger-4" data-id="element-910">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={finalVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-911" />
      </div>
    </div>;
}